import { customRound } from "@gameApp/helpers/numbers/customRound";
import { PRICE_LOCALE } from "@gameApp/helpers/price/consts";
import { priceGetMaxFractionDigits } from "@gameApp/helpers/price/priceGetMaxFractionDigits";
import type { DivisorNames } from "@gameApp/helpers/price/types";

export const PRICE_DIVISORS = {
  K: 1000,
  M: 1000 * 1000,
  B: 1000 * 1000 * 1000,
  T: 1000 * 1000 * 1000 * 1000,
};

interface Params {
  price: number;
  divisorName: DivisorNames | "";
  options?: Intl.NumberFormatOptions;
  rounding?: "floor" | "ceil" | "round";
}

/**
 * Returns a divided price (if there is a divisor) with separated numbers by number classes
 * */
export function pricePrepare({
  price,
  divisorName,
  options,
  rounding,
}: Params): string {
  let maximumFractionDigits = priceGetMaxFractionDigits({ price });

  // если снаружи в опциях minimumFractionDigits будет больше текущего maximumFractionDigits, то ошибка
  if (
    options?.minimumFractionDigits &&
    options?.minimumFractionDigits > maximumFractionDigits
  ) {
    maximumFractionDigits = options?.minimumFractionDigits;
  }

  let formattedPrice: number;

  if (Number.isInteger(price)) {
    formattedPrice = price;
  } else if (rounding) {
    formattedPrice = customRound({
      value: price,
      decimalPlaces: maximumFractionDigits,
      roundingDirection: rounding,
    });
  } else {
    formattedPrice = getFormattedPrice(price, maximumFractionDigits);
  }

  // текущие опции + из параметров
  const mergedOptions = {
    useGrouping: true,
    minimumFractionDigits: 2,
    maximumFractionDigits,
    ...options,
  };

  if (divisorName) {
    return (formattedPrice / PRICE_DIVISORS[divisorName]).toLocaleString(
      PRICE_LOCALE,
      mergedOptions,
    );
  }

  return formattedPrice.toLocaleString(PRICE_LOCALE, mergedOptions);
}

function getFormattedPrice(price: number, maxFractionDigits: number): number {
  const stringPrice = String(price);

  const fractionalPart = stringPrice.slice(
    stringPrice.indexOf(".") + 1,
    stringPrice.length,
  );

  if (fractionalPart.length <= maxFractionDigits) {
    return price;
  }

  if (maxFractionDigits >= 8) {
    return +price.toFixed(maxFractionDigits);
  }

  const integer = Math.trunc(price);
  const fractional = fractionalPart.slice(0, maxFractionDigits);

  return +`${integer}.${fractional}`;
}
